import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid
} from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import React, { ChangeEvent, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionButton,
  DeleteButton
} from 'src/components/shared/Button/Button';
import { LoadingPanel } from 'src/components/shared/DataGrid/LoadingPanel';
import { Icon } from 'src/components/shared/Icon/Icon';
import { StatusCircle } from 'src/components/shared/StatusCircle/StatusCircle';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { LabelType } from 'src/components/shared/Typography/LabelType';
import { MixtureDto, MixtureStatus } from 'src/store/mixtures/mixture';
import { formatDate } from 'src/utils/date';
import { DetailsList } from '../Products/ProductsListMobile';

type Props = {
  data: DataResult;
  loading: boolean;
  requestState: DataSourceRequestState;
  onEdit: (mixture: MixtureDto) => void;
  onDelete: (mixture: MixtureDto) => void;
  onPageChange: (skip: number, take: number) => void;
};

export const MixturesListMobile: React.FC<Props> = ({
  data,
  loading,
  requestState,
  onEdit,
  onDelete,
  onPageChange
}) => {
  const { t } = useTranslation();

  return (
    <Fragment>
      <Pagination
        count={Math.ceil(data.total / (requestState.take ?? 20))}
        siblingCount={0}
        onChange={(event: ChangeEvent<unknown>, page: number) => {
          const take = requestState.take ?? 20;
          onPageChange(page * take, take);
        }}
        variant="outlined"
        color="primary"
        className="mb-24"
      />

      {loading && <LoadingPanel />}

      {data.data.map((mixture: MixtureDto) => (
        <Accordion key={mixture.id}>
          <AccordionSummary expandIcon={<Icon type="la-angle-down" />}>
            <Box display="flex" alignItems="center" width="100%">
              <Grid container spacing={0}>
                <Grid item xs={5}>
                  <LabelType>{t('props.number')}</LabelType>
                  <BodyType>{mixture.no}</BodyType>
                </Grid>
                <Grid item xs={7}>
                  <LabelType>{t('mixture.mixDate')}</LabelType>
                  <BodyType>{formatDate(mixture.mixDate)}</BodyType>
                </Grid>
              </Grid>
              <StatusCircle
                color={
                  mixture.mixtureStatusId === MixtureStatus.Finished
                    ? 'var(--green-status-color-rgb)'
                    : 'var(--yellow-status-color-rgb)'
                }
                className="ml-auto"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails style={{ display: 'block' }} className="pr-64">
            <Grid container spacing={0}>
              <Grid item xs={5}>
                <DetailsList>
                  <li>
                    <LabelType>{t('product.runNo')}</LabelType>
                    <BodyType>{mixture.runNo ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('product.lotNo')}</LabelType>
                    <BodyType>{mixture.lotNo ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('runSystem.singular')}</LabelType>
                    <BodyType>{mixture.runSystemName}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('grade.singular')}</LabelType>
                    <BodyType>{mixture.gradeName ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('specification.pspNo')}</LabelType>
                    <BodyType>{mixture.specificationNo ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('specification.psp')}</LabelType>
                    <BodyType>{mixture.specificationName ?? '–'}</BodyType>
                  </li>
                </DetailsList>
              </Grid>

              <Grid item xs={7}>
                <DetailsList>
                  <li>
                    <LabelType>{t('props.totalAmount')}</LabelType>
                    <BodyType>{mixture.totalAmount ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('props.amount')}</LabelType>
                    <BodyType>{mixture.currentAmount ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('props.totalCount')}</LabelType>
                    <BodyType>{mixture.totalNumber ?? '–'}</BodyType>
                  </li>
                  <li>
                    <LabelType>{t('props.count')}</LabelType>
                    <BodyType>{mixture.currentNumber ?? '–'}</BodyType>
                  </li>
                </DetailsList>
              </Grid>
            </Grid>

            <hr />

            <Box display="flex">
              <ActionButton
                icon="la la-pen"
                onClick={() => onEdit(mixture)}
                className="mr-16"
              />

              <DeleteButton onClick={() => onDelete(mixture)} />
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Fragment>
  );
};
